import * as R from 'ramda'
import React, { useCallback, useState } from 'react'

import { isMixed } from 'components/uiKit/KitUtils'
import Divider from 'components/uiKit/Divider'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import { notEmpty } from 'utils/notEmpty'

import { IKitControlProps, KitSize } from '../KitTypes'
import ArrayDragItem from './ArrayDragItem'
import ArrayDragLayout from './ArrayDragLayout'
import ButtonAddItem from './ButtonAddItem'
import Dragline from './Dragline'
import { EMPTY_HOVER } from './constants'
import type { ArrayValue, IArrayChangeMeta, IRenderButtonAddItem, IRenderItemProps } from './types'

export type IArrayControlProps<
  V extends ArrayValue,
  CP extends IRenderItemProps<V> = IRenderItemProps<V>,
> = {
  RenderItem: React.FC<CP>
  RenderAdd?: React.FC<IRenderButtonAddItem<V>> | null
  defaultItem?: Omit<V, 'value'>
  min?: number
  max?: number
  direction?: 'vertical' | 'horizontal'
  size?: KitSize
} & IKitControlProps<V[], IArrayChangeMeta<V>> &
  Omit<Partial<CP>, keyof IRenderItemProps<V>>

const ArrayControl = <V extends ArrayValue, CP extends IRenderItemProps<V> = IRenderItemProps<V>>({
  defaultValue = EMPTY_ARRAY,
  value: rawValue = defaultValue,
  onChange,
  max,
  min,
  name,
  defaultItem,
  RenderItem,
  direction = 'vertical',
  RenderAdd = ButtonAddItem,
  ...rest
}: IArrayControlProps<V, CP>) => {
  const value = isMixed(rawValue) ? (EMPTY_ARRAY as V[]) : (rawValue as V[])
  const [hoverId, setHoverId] = useState<string | null>(EMPTY_HOVER)

  const handleDrop = useCallback(
    (draggedId: string) => {
      const toMove = [value.find((item) => item.value === draggedId) || null]
      const index = hoverId === null ? 0 : value.findIndex((item) => item.value === hoverId) + 1
      const newValue = R.compose(
        R.filter<V | null, V>(notEmpty),
        R.insertAll<V | null>(index, toMove),
        R.map<V, V | null>((item) => (toMove.includes(item) ? null : item)),
      )(value)

      onChange?.(newValue)
    },
    [value, hoverId, onChange],
  )

  return (
    <ArrayDragLayout
      {...rest}
      direction={direction}
      max={max}
      min={min}
      name={name}
      onChange={onChange}
      onDrop={handleDrop}
      RenderItem={RenderItem as unknown as React.FC<IRenderItemProps<V>>}
      value={value}
    >
      {hoverId === null && <Dragline direction={direction} />}
      {value.map((item, index) => (
        <React.Fragment key={item.value}>
          <ArrayDragItem
            {...rest}
            direction={direction}
            hoverId={hoverId}
            index={index}
            item={item}
            max={max}
            min={min}
            name={name}
            onChange={onChange}
            onDrop={handleDrop}
            prevId={value[index - 1]?.value || null}
            RenderItem={RenderItem as unknown as React.FC<IRenderItemProps<V>>}
            setHoverId={setHoverId}
            value={value}
          />

          {hoverId === item.value && <Dragline direction={direction} />}
        </React.Fragment>
      ))}
      {RenderAdd && (
        <>
          <Divider styleType='ghost' free />
          <RenderAdd
            array={value}
            defaultItem={defaultItem}
            max={max}
            min={min}
            name={name}
            onChange={onChange}
          />
        </>
      )}
    </ArrayDragLayout>
  )
}

export default React.memo(ArrayControl) as typeof ArrayControl
